import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { gsap } from "gsap";
import { isStoreProductOutOfStock } from "../../../utils/storeUtils";
import { mediaPercentToContainerCss } from "../../../utils/hotspotCoords";
import { StoreProductPriceBlock } from "./StoreProductPriceBlock";
import "./Masonry.css";

const useMedia = (queries, values, defaultValue) => {
  const get = () =>
    values[queries.findIndex((q) => window.matchMedia(q).matches)] ?? defaultValue;

  const [value, setValue] = useState(get);

  useEffect(() => {
    const handler = () => setValue(get);
    queries.forEach((q) => window.matchMedia(q).addEventListener("change", handler));
    return () =>
      queries.forEach((q) => window.matchMedia(q).removeEventListener("change", handler));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  return value;
};

const useMeasure = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
};

const preloadImages = async (urls, onSize) => {
  await Promise.all(
    urls.map(
      (src) =>
        new Promise((resolve) => {
          if (!src) {
            resolve();
            return;
          }
          const img = new Image();
          img.src = src;
          const done = () => resolve();
          img.onload = () => {
            if (onSize && img.naturalWidth && img.naturalHeight) {
              onSize(src, img.naturalWidth, img.naturalHeight);
            }
            done();
          };
          img.onerror = done;
        })
    )
  );
};

const Masonry = ({
  items = [],
  ease = "power3.out",
  duration = 0.6,
  stagger = 0.05,
  animateFrom = "bottom",
  scaleOnHover = true,
  hoverScale = 0.95,
  blurToFocus = true,
  colorShiftOnHover = false,
  onItemClick,
  onAddToCart,
  onProductNavigate,
}) => {
  const { t } = useTranslation("global");

  /* Desktop ladder unchanged (768px+). Phone / small tablet: always 2 columns. */
  const columns = useMedia(
    [
      "(max-width: 767px)",
      "(min-width: 1500px)",
      "(min-width: 1000px)",
      "(min-width: 600px)",
      "(min-width: 400px)",
    ],
    [2, 5, 4, 3, 2],
    2
  );

  /* Shorter tiles on small screens; desktop keeps divisor 2 */
  const heightDivisor = useMedia(["(max-width: 767px)"], [2.65], 2);

  const [containerRef, { width }] = useMeasure();
  const [imagesReady, setImagesReady] = useState(false);
  const [hotspotPlacement, setHotspotPlacement] = useState({});
  const [tileMediaNaturalSize, setTileMediaNaturalSize] = useState({});

  const applyHotspotPlacement = useCallback((key, el) => {
    if (!el || typeof window === "undefined") return;
    const rect = el.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const aboveFold = mid < window.innerHeight * 0.5;
    setHotspotPlacement((prev) => {
      const next = aboveFold ? "down" : "up";
      if (prev[key] === next) return prev;
      return { ...prev, [key]: next };
    });
  }, []);

  useEffect(() => {
    let active = true;
    setImagesReady(false);
    setTileMediaNaturalSize({});
    preloadImages(items.map((i) => i.img), (src, w, h) => {
      if (!active) return;
      const match = items.find((i) => i.img === src);
      if (match?.id) {
        setTileMediaNaturalSize((prev) => ({
          ...prev,
          [match.id]: { w, h },
        }));
      }
    }).then(() => {
      if (active) setImagesReady(true);
    });
    return () => {
      active = false;
    };
  }, [items]);

  const registerTileVideoSize = useCallback((itemId, el) => {
    if (!itemId || !el) return;
    const w = el.videoWidth;
    const h = el.videoHeight;
    if (!w || !h) return;
    setTileMediaNaturalSize((prev) => {
      const cur = prev[itemId];
      if (cur?.w === w && cur?.h === h) return prev;
      return { ...prev, [itemId]: { w, h } };
    });
  }, []);

  const grid = useMemo(() => {
    if (!width) return [];

    const colHeights = new Array(columns).fill(0);
    const columnWidth = width / columns;
    /* Wider columns (big desktop / iMac) = same px height feels “short”; scale height gently with column width. */
    const refColPx = 260;
    const layoutHeightBoost = Math.min(1.38, Math.max(1, columnWidth / refColPx));

    return items.map((child) => {
      const span = Math.max(1, Math.min(columns, Number(child.span || 1)));
      let col = 0;
      let y = Number.POSITIVE_INFINITY;

      for (let c = 0; c <= columns - span; c += 1) {
        const candidateY = Math.max(...colHeights.slice(c, c + span));
        if (candidateY < y) {
          y = candidateY;
          col = c;
        }
      }

      if (child.alignRight && colHeights.every((h) => h === 0)) {
        col = Math.max(0, columns - span);
        y = 0;
      }

      const x = columnWidth * col;
      const baseH = Math.max(100, Number(child.height || 320) / heightDivisor);
      const height = Math.round(baseH * layoutHeightBoost);
      for (let i = col; i < col + span; i += 1) {
        colHeights[i] = y + height;
      }
      return { ...child, x, y, w: columnWidth * span, h: height };
    });
  }, [columns, heightDivisor, items, width]);

  const containerHeight = useMemo(
    () => (grid.length ? Math.max(...grid.map((item) => item.y + item.h)) : 0),
    [grid]
  );

  const getInitialPosition = (item) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return { x: item.x, y: item.y };

    let direction = animateFrom;
    if (animateFrom === "random") {
      const directions = ["top", "bottom", "left", "right"];
      direction = directions[Math.floor(Math.random() * directions.length)];
    }

    switch (direction) {
      case "top":
        return { x: item.x, y: -200 };
      case "bottom":
        return { x: item.x, y: window.innerHeight + 200 };
      case "left":
        return { x: -200, y: item.y };
      case "right":
        return { x: window.innerWidth + 200, y: item.y };
      case "center":
        return {
          x: containerRect.width / 2 - item.w / 2,
          y: containerRect.height / 2 - item.h / 2,
        };
      default:
        return { x: item.x, y: item.y + 100 };
    }
  };

  const hasMounted = useRef(false);

  useLayoutEffect(() => {
    if (!imagesReady) return;

    grid.forEach((item, index) => {
      const selector = `[data-key="${item.id}"]`;
      const animationProps = {
        x: item.x,
        y: item.y,
        width: item.w,
        height: item.h,
      };

      if (!hasMounted.current) {
        const initialPos = getInitialPosition(item);
        const initialState = {
          opacity: 0,
          x: initialPos.x,
          y: initialPos.y,
          width: item.w,
          height: item.h,
          ...(blurToFocus && { filter: "blur(10px)" }),
        };

        gsap.fromTo(selector, initialState, {
          opacity: 1,
          ...animationProps,
          ...(blurToFocus && { filter: "blur(0px)" }),
          duration: 0.8,
          ease: "power3.out",
          delay: index * stagger,
        });
      } else {
        gsap.to(selector, {
          ...animationProps,
          duration,
          ease,
          overwrite: "auto",
        });
      }
    });

    hasMounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, imagesReady, stagger, animateFrom, blurToFocus, duration, ease]);

  const handleMouseEnter = (e, item) => {
    const element = e.currentTarget;
    const selector = `[data-key="${item.id}"]`;

    if (scaleOnHover) {
      gsap.to(selector, {
        scale: hoverScale,
        duration: 0.3,
        ease: "power2.out",
      });
    }

    if (colorShiftOnHover) {
      const overlay = element.querySelector(".color-overlay");
      if (overlay) {
        gsap.to(overlay, { opacity: 0.3, duration: 0.3 });
      }
    }
  };

  const handleHotspotAddToCart = useCallback(
    (e, product) => {
      e.preventDefault();
      e.stopPropagation();
      if (!product?._id || !onAddToCart) return;
      if (isStoreProductOutOfStock(product)) return;
      const colors = Array.isArray(product.colors) ? product.colors : [];
      const first = colors[0];
      onAddToCart(product, {
        selectedColor: colors.length > 0 ? first?.name : undefined,
        selectedColorHex: first?.hex || first?.code || undefined,
      });
    },
    [onAddToCart]
  );

  const handleHotspotCardNavigate = useCallback(
    (e, productId) => {
      e.preventDefault();
      e.stopPropagation();
      if (!productId || !onProductNavigate) return;
      onProductNavigate(productId);
    },
    [onProductNavigate]
  );

  const handleMouseLeave = (e, item) => {
    const element = e.currentTarget;
    const selector = `[data-key="${item.id}"]`;

    if (scaleOnHover) {
      gsap.to(selector, {
        scale: 1,
        duration: 0.3,
        ease: "power2.out",
      });
    }

    if (colorShiftOnHover) {
      const overlay = element.querySelector(".color-overlay");
      if (overlay) {
        gsap.to(overlay, { opacity: 0, duration: 0.3 });
      }
    }
  };

  return (
    <div ref={containerRef} className="list" style={{ height: containerHeight }}>
      {grid.map((item) => {
        const spots =
          Array.isArray(item.hotspots) && item.hotspots.length > 0
            ? item.hotspots
            : item.hotspot
            ? [item.hotspot]
            : [];

        return (
          <div
            key={item.id}
            data-key={item.id}
            className="item-wrapper"
            onClick={() =>
              onItemClick ? onItemClick(item) : window.open(item.url, "_blank", "noopener")
            }
            onMouseEnter={(e) => handleMouseEnter(e, item)}
            onMouseLeave={(e) => handleMouseLeave(e, item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onItemClick?.(item);
              }
            }}
            aria-label={`View ${item.label || "collection"}`}
          >
            <div className="item-img">
              {item.video ? (
                <video
                  className="item-video"
                  src={item.video}
                  poster={item.img || undefined}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(ev) => registerTileVideoSize(item.id, ev.currentTarget)}
                />
              ) : (
                <div
                  className="item-img-bg"
                  style={{ backgroundImage: `url("${item.img}")` }}
                />
              )}
              {spots.map((spot, si) => {
                const hx = Number(spot?.x);
                const hy = Number(spot?.y);
                const safeHotspotX = Number.isFinite(hx) ? Math.min(100, Math.max(0, hx)) : 50;
                const safeHotspotY = Number.isFinite(hy) ? Math.min(100, Math.max(0, hy)) : 50;
                const hsKey = `${item.id}-hs-${si}`;
                const hotspotOutOfStock =
                  spot.product && isStoreProductOutOfStock(spot.product);
                const mediaSize = tileMediaNaturalSize[item.id];
                const tilePad = 12;
                const containerW = Math.max(1, item.w - tilePad);
                const containerH = Math.max(1, item.h - tilePad);
                const hotspotPos = mediaSize
                  ? mediaPercentToContainerCss(
                      safeHotspotX,
                      safeHotspotY,
                      containerW,
                      containerH,
                      mediaSize.w,
                      mediaSize.h,
                      "cover"
                    )
                  : { left: `${safeHotspotX}%`, top: `${safeHotspotY}%` };
                return (
                  <div
                    key={`${item.id}-hs-${si}`}
                    className="item-hotspot"
                    role="group"
                    aria-label={spot.title ? `Product: ${spot.title}` : "Product hotspot"}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") e.stopPropagation();
                    }}
                    style={{
                      ...hotspotPos,
                      zIndex: 2 + si,
                    }}
                    onMouseEnter={(e) => applyHotspotPlacement(hsKey, e.currentTarget)}
                    onFocus={(e) => applyHotspotPlacement(hsKey, e.currentTarget)}
                  >
                    <span className="item-hotspot-dot" aria-hidden="true" />
                    <div
                      data-placement={hotspotPlacement[hsKey] || "up"}
                      className={`item-hotspot-card${
                        spot.product?._id && onProductNavigate ? " item-hotspot-card--clickable" : ""
                      }`}
                      onClick={
                        spot.product?._id && onProductNavigate
                          ? (e) => handleHotspotCardNavigate(e, spot.product._id)
                          : undefined
                      }
                      onKeyDown={
                        spot.product?._id && onProductNavigate
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                onProductNavigate(spot.product._id);
                              }
                            }
                          : undefined
                      }
                      tabIndex={spot.product?._id && onProductNavigate ? 0 : undefined}
                      aria-label={
                        spot.product?._id && onProductNavigate
                          ? `View ${spot.title || "product"} details`
                          : undefined
                      }
                    >
                      {spot.image ? (
                        <div className="item-hotspot-media">
                          <img
                            className="item-hotspot-thumb"
                            src={spot.image}
                            alt={spot.title ? `Product: ${spot.title}` : "Product"}
                            loading="lazy"
                            decoding="async"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </div>
                      ) : null}
                      <div className="item-hotspot-body">
                        <span className="item-hotspot-title">{spot.title}</span>
                        {spot.product ? (
                          <div className="item-hotspot-price">
                            <StoreProductPriceBlock
                              product={spot.product}
                              showDiscountPercent={false}
                            />
                          </div>
                        ) : null}
                        {spot.details ? (
                          <span className="item-hotspot-details">{spot.details}</span>
                        ) : null}
                      </div>
                      <div className="item-hotspot-footer">
                        {hotspotOutOfStock ? (
                          <span className="item-hotspot-stock-badge" role="status">
                            {t("store.outOfStock", { defaultValue: "Out of stock" })}
                          </span>
                        ) : spot.product && onAddToCart ? (
                          <button
                            type="button"
                            className="item-hotspot-add-btn"
                            aria-label={`Add ${spot.title || "product"} to cart`}
                            onClick={(e) => handleHotspotAddToCart(e, spot.product)}
                          >
                            {t("store.addToCart", { defaultValue: "Add to cart" })}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
              {colorShiftOnHover ? <div className="color-overlay" /> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Masonry;
